import { Button, Card } from 'react-bootstrap';
import { BsCheckLg } from 'react-icons/bs';
import { getOrderedSizes } from '../../utils/layout';

const WidgetStylesTray = ({
  expandedWidget,
  availableSizes,
  isStyleSelected,
  toggleStyleSelection,
  isCompactLayout = false,
}) => {
  const widgetTitle = expandedWidget.displayName || expandedWidget.name || 'Widget';
  const orderedSizes = getOrderedSizes(availableSizes);

  const getSizeDimensions = (size) => {
    const [width, height] = String(size)
      .split('x')
      .map((value) => Number(value));

    return {
      width: Number.isFinite(width) && width > 0 ? width : 1,
      height: Number.isFinite(height) && height > 0 ? height : 1,
    };
  };

  const maxSizeWidthUnits = orderedSizes.reduce(
    (maxWidth, size) => Math.max(maxWidth, getSizeDimensions(size).width),
    1,
  );
  const shouldPreferStackedLayout =
    orderedSizes.length <= 3 &&
    orderedSizes.includes('2x1') &&
    (orderedSizes.includes('1x1') || orderedSizes.includes('1x2'));

  const gridColumns = shouldPreferStackedLayout
    ? 3
    : Math.max(4, Math.min(6, maxSizeWidthUnits * 2));
  const resolvedGridColumns = isCompactLayout ? 1 : gridColumns;

  const getPreviewObjectPosition = (size) => {
    const { width, height } = getSizeDimensions(size);
    if (height > width) {
      return 'center top';
    }
    return 'center center';
  };

  const renderSizeTile = (size) => {
    const previewImage = expandedWidget.previewImages?.[size];
    const selected = isStyleSelected(expandedWidget.id, size);
    const tileBorderClass = selected ? 'border-white' : 'border-white border-opacity-25';
    const sizeDimensions = getSizeDimensions(size);
    const tileColumnSpan = isCompactLayout
      ? 1
      : Math.max(1, Math.min(resolvedGridColumns, sizeDimensions.width));
    const tileRowSpan = isCompactLayout ? 1 : Math.max(1, sizeDimensions.height);

    return (
      <Button
        key={`${expandedWidget.id}:${size}`}
        type="button"
        variant="dark"
        className={`text-start p-1 rounded-3 border bg-base ${tileBorderClass} shadow-sm d-flex flex-column`}
        style={{
          width: '100%',
          maxWidth: 'none',
          minWidth: 0,
          height: '100%',
          gridColumn: `span ${tileColumnSpan}`,
          gridRow: `span ${tileRowSpan}`,
        }}
        data-size={size}
        onClick={() => toggleStyleSelection(expandedWidget.id, size)}
      >
        <div className="position-relative rounded-2 overflow-visible h-100">
          <div className="rounded-2 overflow-hidden h-100">
            <div className="bg-base w-100 h-100">
              {previewImage ? (
                <img
                  src={previewImage}
                  alt={`${expandedWidget.displayName} ${size} preview`}
                  className="d-block w-100 h-100"
                  style={{
                    objectFit: 'contain',
                    objectPosition: getPreviewObjectPosition(size),
                  }}
                />
              ) : (
                <span className="w-100 h-100 d-block" />
              )}
            </div>
          </div>
          <span
            className={`position-absolute d-inline-flex align-items-center justify-content-center rounded-circle ${
              selected ? 'bg-white border border-white' : 'bg-transparent border-0'
            }`}
            style={{ top: -8, right: -8, width: 20, height: 20, lineHeight: 1, zIndex: 2 }}
          >
            {selected ? <BsCheckLg size={12} color="#325277" /> : null}
          </span>
        </div>
      </Button>
    );
  };

  return (
    <div className="position-relative mt-2">
      <Card className="rounded-3 text-white bg-base">
        <Card.Body className="p-2 bg-base rounded-2">
          <div className="mb-2 px-1">
            <span className="d-block fw-semibold" title={widgetTitle}>
              {widgetTitle}
            </span>
          </div>
          <div
            className="d-grid gap-1 justify-content-start align-items-start"
            style={{
              ...(isCompactLayout
                ? {
                    gridTemplateColumns: 'minmax(0, 1fr)',
                  }
                : {
                    '--style-grid-unit': 'clamp(140px, 10.5vw, 176px)',
                    gridTemplateColumns: `repeat(${resolvedGridColumns}, var(--style-grid-unit))`,
                    gridAutoRows: 'var(--style-grid-unit)',
                    gridAutoFlow: 'row dense',
                  }),
            }}
          >
            {orderedSizes.map((size) => renderSizeTile(size))}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default WidgetStylesTray;

